import { BenefitClaimModal } from '@/components/modal/BenefitClaimModal'
import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import type { ResolvedTerm } from '@/services/myBenefits'
import { BenefitTarget, HeldBenefit, useCoachProfile, useMyBenefits } from '@/services/myBenefits'
import { claimableAt } from '@/pages/benefits/claimOptions'
import { SealedFieldsView } from '@/components/sealed/SealedFields'
import { useEntityKinds } from '@/services/coachBenefits'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

/**
 * A coach's published profile — what they say about themselves, and what claiming with them is
 * worth.
 *
 * Addressed by kind and id, the same way the entity API addresses anything, so a URL can be built
 * straight from an entity URN without a lookup table.
 *
 * It deliberately does not restate the player page. Ratings, photo and match history already have a
 * home that every member knows how to read, and this links to it rather than growing a thinner
 * second copy that drifts. What lives here is only what the coach authored and what a claim is
 * worth — the two things the player page has no idea about.
 */
export const CoachProfile = () => {
  const { t } = useTranslation()
  const { kind, entityId } = useParams()
  const id = Number(entityId) || null
  const { data: profile, isLoading, isError } = useCoachProfile(kind ?? null, id)
  const { data: benefits } = useMyBenefits()
  // The attribute SHAPE, published by the server. Read rather than assumed: a coaching profile's
  // fields are a sealed type the server owns, and listing them here would be a second copy that
  // silently stops matching it.
  const { data: kinds } = useEntityKinds()
  const [claiming, setClaiming] = useState<HeldBenefit | null>(null)

  // The oldest benefit this member holds that names this kind of coach — for a bundle, the part that
  // does. Never one that names somewhere else: the coach is the target, so it must be a place the
  // benefit can go.
  const claimable = profile ? claimableAt(benefits ?? [], profile.kind) : null

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-white text-opacity-70">{t('coach.loading')}</p>
  }

  // A profile that will not load and a coach who has withdrawn theirs are the same answer from
  // here, and deliberately so: the server returns not-found for a suspended entity precisely so a
  // passing member cannot tell the two apart.
  if (isError || !profile) {
    return (
      <div className="mx-auto mt-10 max-w-2xl rounded-lg bg-white p-8 text-center">
        <p className="text-base font-medium text-gray-800">{t('coach.notFoundTitle')}</p>
        <p className="mt-1 text-sm text-gray-500">{t('coach.notFoundDesc')}</p>
      </div>
    )
  }

  const { operator, attributes, terms } = profile
  const attributeSchema = kinds?.find((entry) => entry.kind === profile.kind)?.attributes ?? null
  // The one field promoted out of the generic block, because it is the page's subtitle rather than
  // a detail: it sits beside the name, in the header's own type. Everything else the variant
  // declares renders below, from the schema, in the order the server declares it.
  const attributeValues = (attributes ?? null) as Record<string, unknown> | null
  const text = (key: string) => (typeof attributeValues?.[key] === 'string' ? (attributeValues[key] as string) : null)
  const headline = text('headline')
  // Promoted for the same reason as the headline: a bio is a paragraph rather than a labelled row,
  // and a website is a thing to click. `SealedFieldsView` renders whatever is left, so a field the
  // server adds still appears here with no release.
  const bio = text('bio')
  const website = text('website')
  const target: BenefitTarget | null = operator
    ? { kind: 'coach', id: operator.playerId, name: operator.name, urn: profile.urn }
    : null

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {/* The header sits on the app's blue gradient, so it is written for that ground; everything
          below it lives on white cards of its own. */}
      <header className="flex items-start gap-4">
        {/* The operator's photo. An entity has none of its own and should not grow one — a coaching
            profile is not a second person to keep a picture of. Initials when they have none:
            a repeated silhouette tells a member nothing. */}
        <Avatar name={operator?.name ?? ''} imageUrl={operator?.imageUrl ?? null} />
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-white text-opacity-70">{profile.kindLabel}</p>
          <h1 className="mt-1 text-2xl font-bold text-white">{operator?.name ?? t('coach.unnamed')}</h1>
          {headline && <p className="mt-1 text-base text-white text-opacity-90">{headline}</p>}
          {operator && (
            <Link
              to={`${ROUTE_PLAYER_PROFILE}/${operator.playerId}`}
              className="mt-2 inline-block text-sm text-white text-opacity-80 underline hover:text-opacity-100"
            >
              {t('coach.viewPlayerProfile')}
            </Link>
          )}
        </div>
      </header>

      {/* What a claim here is worth. First, because it is the question that brought the member to
          this page — everything below is them deciding whether they want it from this person. */}
      {terms.length > 0 && (
        <section className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('coach.offersTitle')}</h2>
          <ul className="mt-2 space-y-1">
            {terms.map((term: ResolvedTerm) => (
              <li key={term.key} className="text-lg font-semibold text-gray-900">
                {term.display}
              </li>
            ))}
          </ul>

          {claimable && target && (
            <>
              <button
                className="mt-3 w-full rounded-full bg-[#3A79E0] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2D5FB8] sm:w-auto"
                onClick={() => setClaiming(claimable)}
              >
                {t('coach.claimHere')}
              </button>
              {/* Claiming reserves; the session is arranged with the coach afterwards. Said here
                  because this is where someone decides, not on the screen after. */}
              <p className="mt-2 text-xs text-gray-500">{t('coach.claimHereHint')}</p>
            </>
          )}
        </section>
      )}

      {bio && (
        <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('coach.aboutTitle')}</h2>
          <p className="mt-2 whitespace-pre-line text-sm text-gray-700">{bio}</p>
        </section>
      )}

      {website && (
        <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('coach.websiteTitle')}</h2>
          <a
            href={hrefFor(website)}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-1 inline-block break-all text-sm text-[#2D5FB8] underline-offset-2 hover:underline"
          >
            {website}
          </a>
        </section>
      )}

      {attributeSchema && attributeValues && (
        <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
          <SealedFieldsView schema={attributeSchema} value={attributeValues} omit={['headline', 'bio', 'website']} />
        </section>
      )}

      <BenefitClaimModal
        open={claiming !== null}
        onClose={() => setClaiming(null)}
        benefit={claiming}
        // The coach is the whole reason this page was open, so there is nothing to pick — but the
        // server is still the authority on whether they accept this benefit, and its refusal is
        // what the modal surfaces if they do not.
        fixedTarget={target}
        onClaimed={null}
        selectedTargetUrn={null}
        onSelectTarget={null}
      />
    </div>
  )
}

/**
 * A coach's own link, made clickable.
 *
 * The value is authored free text, so it routinely arrives without a scheme — "example.com" is what
 * a person types, and a bare href would resolve relative to this app. Prefixed rather than
 * validated: a coach who typed something unopenable gets a dead link, which beats refusing to show
 * the address they chose to publish.
 */
const hrefFor = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`)

/** Up to two initials, from the first and last word of a name. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()

const Avatar = ({ name, imageUrl }: { name: string; imageUrl: string | null }) =>
  imageUrl ? (
    <img src={imageUrl} alt="" className="h-16 w-16 shrink-0 rounded-full object-cover" />
  ) : (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white/20 text-lg font-semibold text-white">
      {initialsOf(name)}
    </div>
  )
